import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { formatDate, formatSerial } from '../lib/utils';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';

const { FiBox, FiCheckCircle, FiActivity, FiAlertCircle, FiBriefcase, FiPlus } = FiIcons;

const CHART_COLORS = {
  acquisitions: '#6366f1',
  dispositions: '#14b8a6',
};

const PIE_COLORS = ['#6366f1', '#14b8a6'];

function formatChartDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function TransactionCharts({ stats }) {
  const chartData = (stats?.chartData || []).map(d => ({
    ...d,
    label: formatChartDate(d.date),
  }));

  const pieData = [
    { name: 'Acquisitions', value: stats?.totalAcquisitions30d || 0 },
    { name: 'Dispositions', value: stats?.totalDispositions30d || 0 },
  ].filter(d => d.value > 0);

  const hasData = chartData.some(d => d.acquisitions > 0 || d.dispositions > 0);

  if (!hasData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Transactions Over 30 Days</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-48 text-slate-400 text-sm">
            No transaction data in the last 30 days.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle>Transactions Over 30 Days</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <defs>
                  <linearGradient id="fillAcq" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS.acquisitions} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={CHART_COLORS.acquisitions} stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="fillDisp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS.dispositions} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={CHART_COLORS.dispositions} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-slate-200 dark:text-slate-800" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11 }}
                  stroke="currentColor"
                  className="text-slate-400"
                  interval="preserveStartEnd"
                  tickLine={false}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 11 }}
                  stroke="currentColor"
                  className="text-slate-400"
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--background, #fff)',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                  labelFormatter={(label) => `Date: ${label}`}
                />
                <Legend
                  iconType="square"
                  wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }}
                  formatter={(value) => <span className="text-slate-600 dark:text-slate-400">{value}</span>}
                />
                <Area
                  type="monotone"
                  dataKey="acquisitions"
                  name="Acquisitions"
                  stroke={CHART_COLORS.acquisitions}
                  strokeWidth={2}
                  fill="url(#fillAcq)"
                  dot={false}
                  activeDot={{ r: 4 }}
                />
                <Area
                  type="monotone"
                  dataKey="dispositions"
                  name="Dispositions"
                  stroke={CHART_COLORS.dispositions}
                  strokeWidth={2}
                  fill="url(#fillDisp)"
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>30 Day Total By Type</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-72 flex flex-col items-center justify-center">
            {pieData.length === 0 ? (
              <p className="text-slate-400 text-sm">No data</p>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={0}
                      outerRadius={80}
                      dataKey="value"
                      stroke="none"
                    >
                      {pieData.map((entry, idx) => (
                        <Cell key={entry.name} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'var(--background, #fff)',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '12px',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex items-center gap-4 mt-2">
                  {pieData.map((entry, idx) => (
                    <div key={entry.name} className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400">
                      <div className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }} />
                      <span>{entry.name}</span>
                      <span className="font-semibold text-slate-900 dark:text-white">({entry.value})</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function Dashboard() {
  const { tenant, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role === 'SUPER_ADMIN';

  useEffect(() => {
    if (tenant?.id) {
      api.getStats(tenant.id)
        .then(data => {
          setStats(data);
          setLoading(false);
        })
        .catch(err => {
          console.error("Dashboard stats error:", err);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [tenant]);

  if (loading) return (
    <div className="flex h-[60vh] items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent"></div>
    </div>
  );

  // Super Admin view (System Overview)
  if (isAdmin) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-2">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">System Overview</h2>
          <p className="text-slate-500 text-lg">Logged in as Global Administrator</p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <Card className="p-2 border-primary-100 dark:border-primary-900 shadow-md">
            <CardHeader>
              <div className="h-12 w-12 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mb-4">
                <SafeIcon icon={FiBriefcase} className="h-6 w-6 text-primary-600" />
              </div>
              <CardTitle>Manage Tenants</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500 mb-6">Create and manage FFL businesses, licenses, and system access.</p>
              <Link to="/admin/tenants">
                <Button className="w-full">Open Tenant Manager</Button>
              </Link>
            </CardContent>
          </Card>

          <Card className="p-2">
            <CardHeader>
              <div className="h-12 w-12 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                <SafeIcon icon={FiActivity} className="h-6 w-6 text-slate-600" />
              </div>
              <CardTitle>Platform Audit Logs</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500 mb-6">Review system-wide compliance records and administrative actions.</p>
              <Button variant="secondary" className="w-full" disabled>Coming Soon</Button>
            </CardContent>
          </Card>
        </div>

        {tenant && stats && <TransactionCharts stats={stats} />}

        {!tenant && (
          <div className="mt-8 p-6 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-900/30">
            <div className="flex gap-4">
              <SafeIcon icon={FiAlertCircle} className="h-6 w-6 text-blue-600 mt-1" />
              <div>
                <h4 className="font-bold text-blue-900 dark:text-blue-100">Setup Required</h4>
                <p className="text-blue-700 dark:text-blue-300 text-sm mt-1">
                  You are a Super Admin, but you haven't selected or been assigned to a specific FFL tenant yet.
                  Go to <strong>Tenant Management</strong> to create your first FFL business.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Standard User with no tenant
  if (!tenant) return (
    <div className="text-center p-12 bg-white dark:bg-slate-900 rounded-xl border border-dashed border-slate-300 dark:border-slate-800">
      <SafeIcon icon={FiBriefcase} className="h-12 w-12 text-slate-300 mx-auto mb-4" />
      <h3 className="text-lg font-bold">No FFL Assigned</h3>
      <p className="text-slate-500 max-w-sm mx-auto mt-2">
        Your account is not currently associated with an FFL business. Please contact a System Administrator.
      </p>
    </div>
  );

  // Standard Dashboard for Tenant Users
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Dashboard</h2>
        <div className="text-sm text-slate-500">
          Tenant: <span className="font-medium text-slate-700 dark:text-slate-300">{tenant.company_name}</span>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Inventory</CardTitle>
            <SafeIcon icon={FiBox} className="h-4 w-4 text-slate-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.inHouse || 0}</div>
            <p className="text-xs text-slate-500">Active firearms in stock</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Dispositions</CardTitle>
            <SafeIcon icon={FiCheckCircle} className="h-4 w-4 text-slate-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.disposed || 0}</div>
            <p className="text-xs text-slate-500">Lifetime dispositions</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Recent Activity</CardTitle>
            <SafeIcon icon={FiActivity} className="h-4 w-4 text-slate-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.recent?.length || 0}</div>
            <p className="text-xs text-slate-500">Actions in last 30 days</p>
          </CardContent>
        </Card>
      </div>

      <TransactionCharts stats={stats} />

      <Card className="col-span-3">
        <CardHeader>
          <CardTitle>Recent Bound Book Entries</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-800 dark:text-slate-400">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Manufacturer</th>
                  <th className="px-4 py-3">Model</th>
                  <th className="px-4 py-3">Serial</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {stats?.recent?.map((item) => (
                  <tr key={item.id} className="bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800">
                    <td className="px-4 py-3 font-medium">{formatDate(item.acquisition_date)}</td>
                    <td className="px-4 py-3">{item.manufacturer}</td>
                    <td className="px-4 py-3">{item.model}</td>
                    <td className="px-4 py-3 font-mono">{formatSerial(item.serial_number, item.is_non_serialized)}</td>
                    <td className="px-4 py-3">
                      <Badge variant={item.status === 'IN_HOUSE' ? 'success' : 'default'}>
                        {item.status.replace('_', ' ')}
                      </Badge>
                    </td>
                  </tr>
                ))}
                {(!stats?.recent || stats.recent.length === 0) && (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-slate-500">No recent activity</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
